// The browser end of the studio's signaling socket: authenticates with the
// studio token as its first frame, reconnects with backoff when the line
// drops (a deploy, a network blip), and holds outgoing messages until the
// server has welcomed us into the room.

export type ServerMessage = { type: string } & Record<string, unknown>;
type Handler = (msg: ServerMessage) => void;

export type ConnectionState = 'connecting' | 'open' | 'closed';

export interface SignalingOptions {
  url: string;
  token: string;
  WebSocketImpl?: typeof WebSocket;
  /** Delays between reconnect attempts; the last one repeats. */
  backoffMs?: number[];
}

/** Close codes after which reconnecting is pointless. */
const FINAL_CODES = new Set([4401, 4003, 1000]);

export function studioWsUrl(origin: string = window.location.origin): string {
  return `${origin.replace(/^http/, 'ws')}/api/studio/ws`;
}

export class SignalingClient {
  state: ConnectionState = 'closed';
  private ws: WebSocket | null = null;
  private welcomed = false;
  private stopped = false;
  private attempt = 0;
  private outbox: string[] = [];
  private handlers = new Map<string, Set<Handler>>();
  private retry: ReturnType<typeof setTimeout> | null = null;

  constructor(private opts: SignalingOptions) {}

  /** Subscribe to a message type ('*' for all, 'state' for connection changes). */
  on(type: string, fn: Handler): () => void {
    let set = this.handlers.get(type);
    if (!set) {
      set = new Set();
      this.handlers.set(type, set);
    }
    set.add(fn);
    return () => set.delete(fn);
  }

  private emit(msg: ServerMessage) {
    for (const fn of this.handlers.get(msg.type) ?? []) fn(msg);
    for (const fn of this.handlers.get('*') ?? []) fn(msg);
  }

  private setState(state: ConnectionState, extra: Record<string, unknown> = {}) {
    this.state = state;
    this.emit({ type: 'state', state, ...extra });
  }

  connect(): void {
    this.stopped = false;
    this.welcomed = false;
    const Impl = this.opts.WebSocketImpl ?? WebSocket;
    const ws = new Impl(this.opts.url);
    this.ws = ws;
    this.setState('connecting');
    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'auth', token: this.opts.token }));
    };
    ws.onmessage = (event: MessageEvent) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(String(event.data)) as ServerMessage;
      } catch {
        return;
      }
      if (msg.type === 'welcome') {
        this.welcomed = true;
        this.attempt = 0;
        this.setState('open');
        for (const m of this.outbox.splice(0)) ws.send(m);
      }
      if (msg.type === 'kicked') this.stopped = true;
      this.emit(msg);
    };
    ws.onclose = (event: CloseEvent) => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.welcomed = false;
      const final = this.stopped || FINAL_CODES.has(event.code);
      this.setState('closed', { code: event.code, final });
      if (final) return;
      const delays = this.opts.backoffMs ?? [500, 1000, 2000, 5000, 10000];
      const delay = delays[Math.min(this.attempt, delays.length - 1)]!;
      this.attempt++;
      this.retry = setTimeout(() => this.connect(), delay);
    };
  }

  /** Send now if we're in the room, else when we are. */
  send(msg: Record<string, unknown>): void {
    const raw = JSON.stringify(msg);
    if (this.ws && this.welcomed) this.ws.send(raw);
    else this.outbox.push(raw);
  }

  close(): void {
    this.stopped = true;
    if (this.retry) clearTimeout(this.retry);
    this.ws?.close(1000);
  }
}
